import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  listenModeles,
  deleteModele,
  getModelesPage,
  resolveModeleConfigSource,
} from "../services/data";
import ShareLinksModal from "../components/ShareLinksModal.jsx";

export default function Modeles({
  user,
  onGoUpload,
  onUse,
  onConfigure, // (fallback temporaire si le parent ne passe pas encore le nouveau prop)
  onAddToMarketplace, // ✅ nouveau prop 
}) {
  const [q, setQ] = useState("");
  const [filters, setFilters] = useState({
    type: "Tous les types", // Contrat | Devis | Facture | Lettre
    source: "Toutes les sources", // Personnalisé | Bibliothèque | Premium
    dossier: "Tous les dossiers",
  });
  const [rows, setRows] = useState([]);
  const [mdlCursor, setMdlCursor] = useState(null);
  const [isMoreLoading, setIsMoreLoading] = useState(false);
  // juste après useState
  const [availableDossiers, setAvailableDossiers] = useState([]);
  const [shareOpen, setShareOpen] = useState(false);
  const [shareTarget, setShareTarget] = useState(null);
  const loadMoreModeles = useCallback(async () => {
    if (!user?.uid || !mdlCursor) return;
    setIsMoreLoading(true);
    const { items, cursor } = await getModelesPage(user.uid, {
      afterDoc: mdlCursor,
      pageSize: 20,
    });
    setMdlCursor(cursor);
    setRows((prev) => {
      const map = new Map(prev.map((x) => [x.id, x]));
      items.forEach((x) => {
        if (!map.has(x.id)) map.set(x.id, x);
      });
      return Array.from(map.values());
    });
    setIsMoreLoading(false);
  }, [user?.uid, mdlCursor]);
  // au montage -> récupérer depuis localStorage
  useEffect(() => {
    // [fire] Firebase-only : charge depuis Firestore
    import("../services/data").then(({ loadAppSettings }) => {
      if (!user?.uid) return;
      loadAppSettings(user.uid).then((app) => {
        const types = (app?.dossiers?.types || []).map((t) => ({
          key: t.key,
          label: t.label || t.name || t.key,
          icon: t.icon || "fas fa-folder",
        }));
        setAvailableDossiers(types);
      });
    });
  }, [user?.uid]);

  // Fonction pour l'édition
  const onEdit = (row) => {
    onGoUpload(row); // Passe le modèle à éditer
  };

  // ✅ Nouveau: handler d'ajout au marketplace (sécurisé et rétro-compatible)
  const onAddToMarketplaceClick = (row) => {
    // Optionnel: confirmation rapide pour éviter un clic accidentel
    // const ok = confirm(`Ajouter « ${row.nom} » au marketplace ?`);
    // if (!ok) return;
    if (typeof onAddToMarketplace === "function") {
      onAddToMarketplace(row);
    } else if (typeof onConfigure === "function") {
      // Fallback: si le parent n'a pas encore câblé onAddToMarketplace,
      // on réutilise temporairement l'ancien flux.
      onConfigure(row);
    }
  };

  const onShareModeleClick = useCallback(
    (row) => {
      if (!user?.uid) return alert("Connecte-toi pour partager.");
      if (!row?.id) return;

      try {
        sessionStorage.setItem("f2d_share_open", "1");
        sessionStorage.setItem("f2d_share_modele_id", row.id);
      } catch (_) {}

      if (onUse) onUse(row);
      else alert("Action 'Utiliser' indisponible.");
    },
    [onUse, user?.uid]
  );

  const openShare = useCallback((m) => {
    setShareTarget(m);
    setShareOpen(true);
  }, []);

  const closeShare = useCallback(() => {
    setShareOpen(false);
    setShareTarget(null);
  }, []);

  // Fonction pour la création (upload normal)
  const onUploadNew = () => {
    onGoUpload(null); // Passe null pour indiquer la création
  };

  const onDelete = async (row) => {
    if (!user?.uid) return;
    const ok = confirm(
      `Supprimer le modèle " ${row.nom} " ?\nCette action supprimera aussi le fichier du stockage.`
    );
    if (!ok) return;
    try {
      await deleteModele(user.uid, {
        id: row.id,
        storagePath: row.storagePath,
        configPath: row.configPath,
      });
      // Pas besoin de gérer l'état local : l'onSnapshot se mettra à jour.
      alert("Modèle supprimé [check]");
    } catch (e) {
      console.error(e);
      alert("Échec de la suppression");
    }
  };

  // Abonnement Firestore
  useEffect(() => {
    if (!user?.uid) return;
    const unsub = listenModeles(user.uid, (data) => setRows(data)); // live: 20 derniers par défaut
    // seed du cursor pour "Voir plus" (hors live)
    let alive = true;
    (async () => {
      const { cursor } = await getModelesPage(user.uid, { pageSize: 20 });
      if (alive) setMdlCursor(cursor);
    })();
    return () => {
      alive = false;
      unsub && unsub();
    };
  }, [user]);

  const filtered = useMemo(() => {
    let data = rows.filter((r) => {
      const query = q.toLowerCase();
      return (
        r.nom.toLowerCase().includes(query) ||
        (r.source || "").toLowerCase().includes(query) ||
        (r.dossier || "").toLowerCase().includes(query)
      );
    });
    if (filters.type !== "Tous les types")
      data = data.filter((r) => r.type === filters.type);
    if (filters.source !== "Toutes les sources")
      data = data.filter((r) => r.source === filters.source);
    if (filters.dossier !== "Tous les dossiers")
      data = data.filter((r) => r.dossier === filters.dossier);
    return data;
  }, [rows, q, filters]);

  useEffect(() => {
    const cards = document.querySelectorAll(".template-card");
    cards.forEach((card, index) => {
      card.style.opacity = 0;
      card.style.transform = "translateY(20px)";
      card.style.transition = `opacity .5s ease ${
        index * 0.06
      }s, transform .5s ease ${index * 0.06}s`;
      requestAnimationFrame(() => {
        card.style.opacity = 1;
        card.style.transform = "translateY(0)";
      });
    });
  }, [filtered]);

  const badgeClass = (source) => {
    if (source === "Personnalisé") return "template-type type-custom";
    if (source === "Premium") return "template-type type-premium";
    return "template-type type-free";
  };
  // Sauvegarder la liste des modèles dans localStorage
  //useEffect(() => {
  //if (rows.length > 0) {
  //localStorage.setItem("all_modeles", JSON.stringify(rows))
  //console.log("[ok] all_modeles mis à jour:", rows)
  //}
  //}, [rows])

  return (
    <div>
      {/* Header */}
      <div className="page-header">
        <h1 className="page-title">Mes Modèles</h1>
        <div className="page-actions">
          <button className="btn btn-outline">
            <i className="fas fa-filter"></i> Filtres
          </button>
          <button className="btn btn-primary" onClick={onUploadNew}>
            <i className="fas fa-upload"></i> Uploader un modèle
          </button>
        </div>
      </div>
      {/* reste de la grille des modèles */}
      {/* Filtres */}
      <div className="filters-section">
        <div className="filters-header">
          <h3 className="filters-title">Filtres avancés</h3>
          <button
            className="btn btn-outline"
            onClick={() =>
              setFilters({
                type: "Tous les types",
                source: "Toutes les sources",
                dossier: "Tous les dossiers",
              })
            }
          >
            <i className="fas fa-times"></i> Réinitialiser
          </button>
        </div>
        <div className="filters-grid">
          <div className="filter-group">
            <label className="filter-label">Source</label>
            <select
              className="filter-select"
              value={filters.source}
              onChange={(e) =>
                setFilters((f) => ({ ...f, source: e.target.value }))
              }
            >
              <option>Toutes les sources</option>
              <option>Personnalisé</option>
              <option>Bibliothèque</option>
              <option>Premium</option>
            </select>
          </div>
          <div className="filter-group">
            <label className="filter-label">Dossier</label>
            <select
              className="filter-select"
              value={filters.dossier}
              onChange={(e) =>
                setFilters((f) => ({ ...f, dossier: e.target.value }))
              }
            >
              <option value="Tous les dossiers">Tous les dossiers</option>
              {availableDossiers.map((d) => (
                <option key={d.key} value={d.key}>
                  {d.label}
                </option>
              ))}
            </select>
          </div>
          <div className="filter-group">
            <label className="filter-label">Recherche</label>
            <input
              className="filter-input"
              type="text"
              placeholder="Rechercher un modèle..."
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Grid */}
      <div className="templates-grid">
        {filtered.map((r, i) => (
          <div className="template-card" key={i}>
            <div className="template-card-header">
              <h3 className="template-card-title">
                <i
                  className={`${
                    /^(fas|far|fal|fab)\sfa-/.test(r.icon || "")
                      ? r.icon
                      : `fas ${r.icon}`
                  }`}
                ></i>{" "}
                {r.nom}
              </h3>
              <span className={badgeClass(r.source)}>
                {r.source === "Bibliothèque"
                  ? "Bibliothèque"
                  : r.source === "Premium"
                  ? "Premium"
                  : "Personnalisé"}
              </span>
            </div>

            <div className="template-card-body">
              <div className="template-preview">
                <i
                  className={`${
                    /^(fas|far|fal|fab)\sfa-/.test(r.icon || "")
                      ? r.icon
                      : `fas ${r.icon}`
                  }`}
                ></i>
              </div>
            </div>

            <div className="template-card-footer">
              <div className="template-info">
                <div className="template-info-item">
                  <i className="fas fa-calendar"></i>
                  <span>Modifié: {r.modifie}</span>
                </div>
                <div className="template-info-item">
                  <i className="fas fa-bolt"></i>
                  <span>Utilisé {r.utilise} fois</span>
                </div>
                <div className="template-info-item">
                  <i className="fas fa-sliders-h"></i>
                  <span>
                    Config : {resolveModeleConfigSource(r).label}
                  </span>
                </div>
              </div>
              <div className="template-actions">
                <div
                  className="action-btn"
                  title="Utiliser"
                  onClick={() => onUse && onUse(r)}
                >
                  <i className="fas fa-play"></i>
                </div>
                <div
                  className="action-btn"
                  title="Partager"
                  onClick={(e) => {
                    e.stopPropagation();
                    openShare(r);
                  }}
                >
                  <i className="fas fa-share-alt"></i>
                </div>
                <div
                  className="action-btn"
                  title="Ajouter au marketplace"
                  onClick={() => onAddToMarketplaceClick(r)}
                >
                  <i className="fas fa-store"></i>
                </div>
                <div
                  className="action-btn"
                  title="Modifier"
                  onClick={() => onEdit(r)}
                >
                  <i className="fas fa-edit"></i>
                </div>
                <div
                  className="action-btn"
                  title="Supprimer"
                  onClick={() => onDelete(r)}
                >
                  <i className="fas fa-trash"></i>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      {mdlCursor && (
        <div
          className="load-more"
          style={{ textAlign: "center", margin: "16px 0" }}
        >
          <button
            className="btn btn-outline"
            disabled={isMoreLoading}
            onClick={loadMoreModeles}
          >
            {isMoreLoading ? "Chargement..." : "Voir plus de modèles"}
          </button>
        </div>
      )}

      {/* Section Bibliothèque (exemple) */}
      <div className="library-section">
        <div className="section-header">
          <h2 className="section-title">Bibliothèque de modèles</h2>
          <div className="section-actions">
            <button
              className="btn btn-outline"
              onClick={() => alert("Voir Premium")}
            >
              <i className="fas fa-crown"></i> Modèles Premium
            </button>
          </div>
        </div>
        <div className="templates-grid">
          <div className="template-card">
            <div className="template-card-header">
              <h3 className="template-card-title">
                <i className="fas fa-file-medical"></i> Rapport médical
              </h3>
              <span className="template-type type-free">Gratuit</span>
            </div>
            <div className="template-card-body">
              <div className="template-preview">
                <i className="fas fa-file-medical"></i>
              </div>
            </div>
            <div className="template-card-footer">
              <div className="template-info">
                <div className="template-info-item">
                  <i className="fas fa-download"></i>
                  <span>325 téléchargements</span>
                </div>
                <div className="template-info-item">
                  <i className="fas fa-star"></i>
                  <span>4.8 (126 avis)</span>
                </div>
              </div>
              <div className="template-actions">
                <div
                  className="action-btn"
                  title="Télécharger"
                  onClick={() => alert("Télécharger")}
                >
                  <i className="fas fa-download"></i>
                </div>
                <div
                  className="action-btn"
                  title="Aperçu"
                  onClick={() => alert("Aperçu")}
                >
                  <i className="fas fa-eye"></i>
                </div>
              </div>
            </div>
          </div>

          <div className="template-card">
            <div className="template-card-header">
              <h3 className="template-card-title">
                <i className="fas fa-gavel"></i> Contrat juridique
              </h3>
              <span className="template-type type-premium">Premium</span>
            </div>
            <div className="template-card-body">
              <div className="template-preview">
                <i className="fas fa-gavel"></i>
              </div>
            </div>
            <div className="template-card-footer">
              <div className="template-info">
                <div className="template-info-item">
                  <i className="fas fa-download"></i>
                  <span>184 téléchargements</span>
                </div>
                <div className="template-info-item">
                  <i className="fas fa-star"></i>
                  <span>4.9 (87 avis)</span>
                </div>
              </div>
              <div className="template-actions">
                <div
                  className="action-btn"
                  title="Acheter"
                  onClick={() => alert("Acheter")}
                >
                  <i className="fas fa-shopping-cart"></i>
                </div>
                <div
                  className="action-btn"
                  title="Aperçu"
                  onClick={() => alert("Aperçu")}
                >
                  <i className="fas fa-eye"></i>
                </div>
              </div>
            </div>
          </div>

          <div className="template-card">
            <div className="template-card-header">
              <h3 className="template-card-title">
                <i className="fas fa-user-tie"></i> CV Professionnel
              </h3>
              <span className="template-type type-free">Gratuit</span>
            </div>
            <div className="template-card-body">
              <div className="template-preview">
                <i className="fas fa-user-tie"></i>
              </div>
            </div>
            <div className="template-card-footer">
              <div className="template-info">
                <div className="template-info-item">
                  <i className="fas fa-download"></i>
                  <span>542 téléchargements</span>
                </div>
                <div className="template-info-item">
                  <i className="fas fa-star"></i>
                  <span>4.7 (214 avis)</span>
                </div>
              </div>
              <div className="template-actions">
                <div
                  className="action-btn"
                  title="Télécharger"
                  onClick={() => alert("Télécharger")}
                >
                  <i className="fas fa-download"></i>
                </div>
                <div
                  className="action-btn"
                  title="Aperçu"
                  onClick={() => alert("Aperçu")}
                >
                  <i className="fas fa-eye"></i>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ShareLinksModal
        open={shareOpen}
        onClose={closeShare}
        user={user}
        template={shareTarget}
      />
    </div>
  );
}
